import React, { type FormEvent, type MutableRefObject, type ReactNode, type RefObject } from 'react';

import * as Sentry from '@sentry/react';
import { ForbiddenError } from 'apps/promo/components/exceptions/ForbiddenError';
import { convertFieldsToSearchParams, validationOnRequiredAndReturnErrorFields } from 'apps/promo/components/form-multi/utils';
import { amplitudeLogout } from 'apps/promo/components/utils/AmplitudeUtils';
import ServerErrorPopup from 'apps/promo/components/utils/ServerErrorPopup';
import { LOCATION, SPRING_REDIRECT_HEADER } from 'apps/promo/constants/Constants';
import { LOGIN } from 'apps/promo/constants/UrlConstants';
import { getFullEncodeApiUrl, processResponseForCloudFlare } from 'apps/promo/helpers/api';
import { logInfo, logWarn } from 'apps/promo/helpers/clientLogger';
import { isSoftOrHardServerError } from 'apps/promo/helpers/utils';

type FieldsMap = Record<string, unknown>;
type PopupHandle = {
    open: (content: ReactNode) => void;
};
type PopupRef = RefObject<PopupHandle>;
type AbortControllerLike = {
    abort?: () => void;
    signal?: AbortSignal;
};

type FormResponse = {
    redirectTo?: string;
    showServerError?: boolean;
    extra?: {
        serverErrorCode?: {
            code?: string | number;
        };
    };
    messageBodyMessages?: unknown;
    authPromoAction?: unknown;
    messages?: {
        errorText?: string;
        buttonClose?: string;
        [key: string]: unknown;
    };
    complexMessages?: unknown;
    countryMenu?: unknown;
    forms?: Record<
        string,
        {
            name?: string;
            fields?: FieldsMap;
            action?: string;
            errors?: Record<string, unknown>;
        }
    >;
    errors?: Record<string, unknown>;
    validateResultErrors?: Record<string, unknown>;
};

type OptionalHandler<TArgs extends unknown[] = []> = ((...args: TArgs) => void) | undefined;

export class FormService {
    #getCanonicalPath: boolean;
    #controller: MutableRefObject<AbortControllerLike | undefined>;
    #formName: string;

    #reduxAction?: string;
    #action?: string;
    #reduxValidateForm?: boolean;
    #validateForm = true;
    #checkNotVisibleField?: boolean;

    #beforeRedirect?: () => void;
    #beforeSubmit?: (event: FormEvent<HTMLFormElement>) => void;
    #onErrorHandler?: (error: unknown) => void | Promise<void>;
    #onSubmitHandler?: (fields: FieldsMap, json: FormResponse) => void | Promise<void>;

    #updateMessages?: OptionalHandler<[unknown]>;
    #updateAuthPromoBlock?: OptionalHandler<[unknown]>;
    #updatePageMessages?: OptionalHandler<[unknown]>;
    #addExtras?: OptionalHandler<[unknown]>;

    #setFieldError?: (formName: string, fieldName: string, error: unknown) => void;
    #setNewForm?: (formName?: string, fields?: FieldsMap, action?: string) => void;
    #changeFormField?: OptionalHandler<[string, string, unknown]>;
    #scrollToField?: (formName: string, fieldName: string) => void;
    #toggleSpinner?: OptionalHandler<[boolean]>;
    #toggleCloudFlareModal?: OptionalHandler<[boolean]>;

    #popupRef?: PopupRef;

    constructor(getCanonicalPath: boolean, controller: MutableRefObject<AbortControllerLike | undefined>, formName: string) {
        this.#getCanonicalPath = getCanonicalPath;
        this.#controller = controller;
        this.#formName = formName;
    }

    setPopups(popupRef: PopupRef): void {
        this.#popupRef = popupRef;
    }

    setAction(reduxAction?: string, action?: string): void {
        this.#reduxAction = reduxAction;
        this.#action = action;
    }

    setFormProps(reduxValidateForm?: boolean, validateForm = true, checkNotVisibleField?: boolean): void {
        this.#reduxValidateForm = reduxValidateForm;
        this.#validateForm = validateForm;
        this.#checkNotVisibleField = checkNotVisibleField;
    }

    setAppAction(
        setFieldError: (formName: string, fieldName: string, error: unknown) => void,
        setNewForm: (formName?: string, fields?: FieldsMap, action?: string) => void,
        changeFormField: OptionalHandler<[string, string, unknown]>,
        scrollToField: (formName: string, fieldName: string) => void,
        toggleSpinner: OptionalHandler<[boolean]>,
        toggleCloudFlareModal: OptionalHandler<[boolean]>,
    ): void {
        this.#setFieldError = setFieldError;
        this.#setNewForm = setNewForm;
        this.#changeFormField = changeFormField;
        this.#scrollToField = scrollToField;
        this.#toggleSpinner = toggleSpinner;
        this.#toggleCloudFlareModal = toggleCloudFlareModal;
    }

    setHandlers(
        beforeRedirect?: () => void,
        beforeSubmit?: (event: FormEvent<HTMLFormElement>) => void,
        onErrorHandler?: (error: unknown) => void | Promise<void>,
        onSubmitHandler?: (fields: FieldsMap, json: FormResponse) => void | Promise<void>,
    ): void {
        this.#beforeRedirect = beforeRedirect;
        this.#beforeSubmit = beforeSubmit;
        this.#onErrorHandler = onErrorHandler;
        this.#onSubmitHandler = onSubmitHandler;
    }

    setUpdateRedux(
        updateMessages: OptionalHandler<[unknown]>,
        updateAuthPromoBlock: OptionalHandler<[unknown]>,
        updatePageMessages: OptionalHandler<[unknown]>,
        addExtras: OptionalHandler<[unknown]>,
    ): void {
        this.#updateMessages = updateMessages;
        this.#updateAuthPromoBlock = updateAuthPromoBlock;
        this.#updatePageMessages = updatePageMessages;
        this.#addExtras = addExtras;
    }

    handlerSubmit(e: FormEvent<HTMLFormElement>, reduxFields: FieldsMap = {}): Promise<string> | void {
        this.#beforeSubmit?.(e);

        e.preventDefault();

        const fields = { ...reduxFields };
        const action = this.getAction();
        if (__isDev__) {
            logInfo('handlerSubmit(e) action: ', action);
        }

        if (fields && action) {
            const isValidateForm = typeof this.#reduxValidateForm === 'boolean' ? this.#reduxValidateForm : this.#validateForm;
            const errorFields = isValidateForm
                ? validationOnRequiredAndReturnErrorFields(this.#formName, fields, this.#changeFormField, this.#checkNotVisibleField)
                : [];

            const canMakeRequest = isValidateForm ? errorFields.length === 0 : true;

            if (canMakeRequest) {
                this.#abort();
                this.#toggleSpinner?.(true);
                void this.#submitForm(fields);
            }
        } else {
            return Promise.resolve(`can't submit :( Reason: fields or action are invalid`);
        }
    }

    #abort = (): void => {
        try {
            this.#controller.current?.abort?.();
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
            logWarn(e);
        } finally {
            this.#controller.current = new AbortController();
        }
    };

    getAction = (): string | undefined => (this.#reduxAction ? this.#reduxAction : this.#action);

    #submitForm(fields: FieldsMap = {}): Promise<void> {
        let responseCode = 200;
        const controller = this.#controller.current;
        const signal = controller && 'signal' in controller ? controller.signal : undefined;
        const options: RequestInit = {
            signal,
            method: 'POST',
            mode: 'cors',
            body: this.#getDataForForm(fields, this.#getCanonicalPath),
            credentials: 'include',
        };
        const actionUrl = getFullEncodeApiUrl(this.getAction() as string);
        return fetch(actionUrl, options)
            .then((response) => {
                responseCode = response.status;
                processResponseForCloudFlare(response, actionUrl, options, {
                    toggleCloudFlareModal: this.#toggleCloudFlareModal,
                });
                this.#processResponseForRedirect(response);
                return response.json() as Promise<FormResponse>;
            })
            .then((json) => {
                if (json) {
                    this.#processJson(json, fields, responseCode);
                }
                this.#abort();
            })
            .catch((error) => {
                logWarn(error);
                if (this.#onErrorHandler) {
                    void Promise.resolve(this.#onErrorHandler(error)).then(() => this.#toggleSpinner?.(false));
                } else {
                    this.#toggleSpinner?.(false);
                    throw error;
                }
            });
    }

    #getDataForForm = (fields: FieldsMap = {}, getCanonicalPath = false): URLSearchParams => {
        return convertFieldsToSearchParams(fields, getCanonicalPath);
    };

    #processResponseForRedirect = (response: Response): void => {
        if (response.status === 403) {
            window.location.replace(LOGIN);
            amplitudeLogout();
            throw new ForbiddenError(`redirect to ${LOGIN}`);
        } else if (response.headers.has(SPRING_REDIRECT_HEADER)) {
            window.location.replace(response.headers.get(SPRING_REDIRECT_HEADER) ?? LOGIN);
            throw new Error(`redirect to ${LOGIN}`);
        } else if (response.headers.has(LOCATION)) {
            window.location.replace(response.headers.get(LOCATION) ?? LOGIN);
            throw new Error(`redirect to ${LOGIN}`);
        }
    };

    #highlightErrorFields(json: FormResponse = {}): void {
        if (json.errors) {
            Object.keys(json.errors).forEach((name) => this.#setFieldError?.(this.#formName, name, json.errors?.[name]));
        }

        if (json.validateResultErrors) {
            const validateErrorFields = Object.keys(json.validateResultErrors);
            validateErrorFields.forEach((name) => this.#setFieldError?.(this.#formName, name, json.validateResultErrors?.[name]));
            if (validateErrorFields.length > 0) {
                this.#scrollToField?.(this.#formName, validateErrorFields[0]);
            }
        }

        const form = json.forms?.[this.#formName];
        if (form?.errors) {
            Object.keys(form.errors).forEach((name) => this.#setFieldError?.(this.#formName, name, form.errors?.[name]));
        }
    }

    #updateRedux(json: FormResponse = {}): void {
        if (json.messageBodyMessages) {
            this.#updateMessages?.(json.messageBodyMessages);
        }

        this.#updateAuthPromoBlock?.(json.authPromoAction);

        if (json.messages) {
            this.#updatePageMessages?.(json.messages);
        }
        if (json.extra) {
            this.#addExtras?.(json.extra);
        }
        const forms = json.forms ?? {};
        const formsNames = Object.keys(forms).filter((fn) => this.#formName !== fn);
        if (formsNames.length > 0) {
            formsNames.forEach((fn) => {
                const newForm = forms[fn];
                this.#setNewForm?.(newForm?.name, newForm?.fields, newForm?.action);
            });
        }
    }

    #processJson = (json: FormResponse = {}, fields: FieldsMap = {}, responseCode = 200): void => {
        if (json.redirectTo) {
            this.#beforeRedirect?.();
            window.location.href = json.redirectTo;
            return;
        }
        const serverErrorCode = json.extra?.serverErrorCode?.code;
        const serverErrorValue =
            typeof serverErrorCode === 'number' ? serverErrorCode : typeof serverErrorCode === 'string' ? Number(serverErrorCode) : undefined;
        if (json.showServerError && !isSoftOrHardServerError(serverErrorValue ?? null)) {
            this.#popupRef?.current?.open(
                <ServerErrorPopup popupRef={this.#popupRef} errorText={json.messages?.errorText} buttonClose={json.messages?.buttonClose} />,
            );
        }

        this.#updateRedux(json);
        this.#highlightErrorFields(json);

        if (responseCode === 200 && this.#onSubmitHandler) {
            void Promise.resolve(this.#onSubmitHandler(fields, json)).then(() => this.#toggleSpinner?.(false));
        } else {
            this.#toggleSpinner?.(false);
        }
    };
}
